import React, { useState, useEffect } from 'react'
import PropTypes from 'prop-types'
import { makeStyles } from '@mui/styles'
import { user } from 'ampersand-app' // FLAG:useDocuAi — read the per-tenant experimental flag
import { experimentalFeatures } from '@/modules/base/util/constants' // FLAG:useDocuAi
import lang from '@/lang'
import ButtonIconSquare from '@/modules/base/components/buttons/icon-square/button-icon-square'
import IconCheck from '@assets/images/icons/check.svg'
import { CssHelper } from '@/modules/base/view/mixin/helpers_mixin'
import { Form, useFormik, FormikProvider } from 'formik'
import * as Yup from 'yup'
import QRPaymentForm from '@/modules/document/components/edit/qr-payment-form'
import QRPaymentSummary from '@/modules/document/components/edit/qr-payment-summary'
import PanelHeader from '@/components/layouts/panel/panel-header'
import Loader from '@/modules/base/components/loader/loader'
import { DEBOUNCE_TIMES } from '@/modules/base/components/buttons'
import { useDispatch, useSelector } from 'react-redux'
import SimpleDrawer from '@/components/drawers/simple-drawer'
import moment from 'moment'
import { Document } from '@/modules/document/types'
import ThemeInterface from '@/theme-interface'
import { updateDocument } from '@/redux/ducks/document/drawer'

const {
  documentPaymentDrawer: {
    title,
    errors,
  },
} = lang

const useStyles = makeStyles((theme: ThemeInterface) => ({
  wrapper: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
    height: '100%',
    '& label': {
      color: theme.labelLight,
    },
  },
  content: {
    padding: CssHelper.px(30, 30, 0, 30),
    display: 'flex',
    flexDirection: 'column',
    flexGrow: 1,
    height: CssHelper.percent(100),
  },
  panelHeader: {
    backgroundColor: theme.backgroundColor,
    minHeight: 85,
    alignItems: 'center',
    justifyContent: 'space-between',
    flexDirection: 'row',
    paddingRight: 15,
    '& svg': {
      fill: CssHelper.important(theme.black),
      height: CssHelper.important(CssHelper.px(12)),
      width: CssHelper.important(CssHelper.px(12)),
    },
  },
  headerLabel: {
    order: -1,
    paddingLeft: 30,
    fontSize: 20,
    color: theme.black,
    whiteSpace: 'nowrap',
    display: 'flex',
    maxWidth: 394,
    flexDirection: 'column',
  },
  formFooter: {
    padding: 20,
    display: 'flex',
    justifyContent: 'flex-end',
    alignItems: 'center',
    backgroundColor: theme.backgroundColor,
    borderTop: theme.border,
    '& > :first-child': {
      marginRight: 20,
    },
  },
  drawer: {
    width: 540,
  },
}))

interface QRPaymentDrawerProps {
  file: Document,
  open?: boolean,
  onClose: () => void,
  onScan: () => void,
  isScanning: boolean,
}

const QRPaymentDrawer = (props: QRPaymentDrawerProps) => {
  const classes = useStyles(props)
  const dispatch = useDispatch()

  const paymentDrawerOpen = useSelector((state) => state.document.drawer.paymentDrawerOpen)

  const [showSummary, setShowSummary] = useState(false)
  const [ibanValid, setIbanValid] = useState(false)

  const {
    onClose,
    onScan,
    isScanning,
    file,
  } = props
  const { id, metaData, documentInvoiceDetails } = file

  const formik = useFormik({
    initialValues: {
      accountHolder: '',
      iban: '',
      bic: '',
      totalAmount: 0,
      purpose: '',
    },
    validationSchema: Yup.object({
      accountHolder: Yup.string().required(errors.accountHolder),
      iban: Yup.string().required(errors.iban),
      totalAmount: Yup.number().required(errors.totalAmount).moreThan(0, errors.negativeAmount),
    }),
    onSubmit: () => {},
  })

  useEffect(() => {
    formik?.setFieldTouched('accountHolder', false)
    formik?.setFieldTouched('iban', false)
    formik?.setFieldTouched('totalAmount', false)
    setShowSummary(false)

    setTimeout(() => {
      formik.setValues({
        accountHolder: metaData?.ocr?.payee ?? documentInvoiceDetails?.accountHolder ?? '',
        iban: metaData?.ocr?.payeeIban ?? documentInvoiceDetails?.iban ?? '',
        bic: metaData?.ocr?.payeeBic ?? documentInvoiceDetails?.bic ?? '',
        totalAmount: metaData?.ocr?.priceTotal ?? documentInvoiceDetails?.totalAmount ?? 0,
        purpose: metaData?.ocr?.receiptNumber ?? documentInvoiceDetails?.invoiceNumber ?? '',
      })
    })
  }, [id, metaData?.ocr, documentInvoiceDetails])

  const onContinue = () => {
    formik?.setFieldTouched('accountHolder')
    formik?.setFieldTouched('iban')
    formik?.setFieldTouched('totalAmount')
    formik?.setFieldTouched('bic')
    formik?.validateForm()

    if (formik?.isValid && ibanValid) {
      setShowSummary(true)
    }
  }

  const close = () => {
    formik.resetForm()
    setShowSummary(false)
    if (onClose) { onClose() }
  }

  const submit = () => {
    const {
      accountHolder,
      iban,
      bic,
      totalAmount,
    } = formik.values
    const value = { ...documentInvoiceDetails, accountHolder, iban, bic, totalAmount, payedAt: moment().toISOString() }
    dispatch(updateDocument({ key: 'documentInvoiceDetails', value }))

    close()
  }

  // FLAG:useDocuAi — there is no scan entry point with the flag ON: EVERY analyze() trigger then
  // starts a real DocuAI run with LLM costs (the route dispatches based on the flag), and automatic
  // analysis fills the fields anyway (the effect above reads metaData.ocr/documentInvoiceDetails).
  // The button is therefore disabled with the flag ON instead of becoming a placebo.
  const useDocuAiEnabled = !!user?.experimentalFeatures?.includes(experimentalFeatures.useDocuAi)

  // Exactly ONE trigger per click: the host owns it (onScan). The former additional
  // DocumentAnalysisService.analyze() call here duplicated the same request.
  const handleScan = () => {
    onScan()
  }

  return (
    <SimpleDrawer className={classes.drawer} noHeader open={paymentDrawerOpen} enableBackdropClick onClose={close}>
      <div className={classes.wrapper}>
        {isScanning && <Loader text={lang.documents.scanning} />}
        <PanelHeader
          onClose={onClose}
          className={classes.panelHeader}
          displayClose>
          <div className={classes.headerLabel}>
            <span>{title}</span>
          </div>
        </PanelHeader>
        {showSummary
          ? <QRPaymentSummary formData={formik.values} />
          : <div className={classes.content}>
            <FormikProvider value={formik} >
              <Form>
                <QRPaymentForm file={file} classes={classes} formik={formik} setIbanValid={setIbanValid} />
              </Form>
            </FormikProvider>
          </div>
        }
        <div className={classes.formFooter}>
          {showSummary
            ? <>
              <ButtonIconSquare debounceTime={DEBOUNCE_TIMES.MINIMAL} label={lang.backButtonLabel} onClick={() => setShowSummary(false)} variant={'outline'} />
              <ButtonIconSquare debounceTime={DEBOUNCE_TIMES.MINIMAL} label={lang.documents.payedButtonLabel} icon={IconCheck} onClick={submit} />
            </>
            : <>
              <ButtonIconSquare disabled={metaData?.ocr || useDocuAiEnabled} debounceTime={DEBOUNCE_TIMES.MINIMAL} label={metaData?.ocr ? lang.documents.scanAgainButtonLabel : lang.documents.scanButtonLabel} onClick={handleScan} variant={'outline'} />
              <ButtonIconSquare debounceTime={DEBOUNCE_TIMES.MINIMAL} label={lang.continueButtonLabel} icon={IconCheck} onClick={onContinue} />
            </>
          }
        </div>
      </div>
    </SimpleDrawer>
  )
}

QRPaymentDrawer.propTypes = {
  file: PropTypes.object,
  open: PropTypes.bool,
  onClose: PropTypes.func,
  onScan: PropTypes.func,
  isScanning: PropTypes.bool,
}

export default QRPaymentDrawer
